import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const FeatureCard = ({ title, description, icon, delay }) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.7, delay, ease: [0.21, 0.47, 0.32, 0.98] }}
    className="glass-card p-8 flex flex-col gap-4"
  >
    <div className="w-12 h-12 rounded-full bg-brand-amber/10 flex items-center justify-center text-brand-amber mb-2">
      <SafeIcon name={icon} className="text-xl" />
    </div>
    <h3 className="font-serif italic text-brand-cream text-2xl">{title}</h3>
    <p className="text-brand-muted leading-relaxed">{description}</p>
  </motion.div>
);

export default function Home() {
  return (
    <div className="flex flex-col gap-24 md:gap-32 pb-16">
      
      {/* Hero Section */}
      <section className="flex flex-col items-center text-center pt-12 md:pt-20 gap-8 max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, ease: "easeOut" }}
        >
          <span className="font-mono uppercase tracking-[0.25em] text-xs text-brand-amber/80 mb-6 block">Available Soon</span>
          <h1 className="font-serif italic text-5xl md:text-7xl lg:text-8xl text-brand-cream tracking-tight leading-[1.1] mb-6">
            Countdowns to<br />what matters.
          </h1>
          <p className="text-brand-muted text-lg md:text-xl leading-relaxed max-w-2xl mx-auto balance-text">
            A beautiful, private countdown app for the moments you are actually waiting for. 
            The trip you booked. A birthday. A deadline. The day it all finally happens.
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="flex flex-col sm:flex-row gap-4 mt-4"
        >
          <a href="#" className="px-8 py-4 bg-brand-cream text-brand-bgStart rounded-full font-medium hover:bg-white transition-colors flex items-center justify-center gap-2">
            <SafeIcon name="Apple" className="text-xl" />
            Download on the App Store
          </a>
          <a href="#" className="px-8 py-4 bg-white/5 border border-white/10 text-brand-cream rounded-full font-medium hover:bg-white/10 transition-colors flex items-center justify-center gap-2">
            <SafeIcon name="Play" className="text-xl" />
            Get it on Google Play
          </a>
        </motion.div>
        <motion.p 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-brand-dim text-sm mt-2"
        >
          Links go live at launch.
        </motion.p>
      </section>

      {/* Features Section */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FeatureCard 
          icon="Clock"
          title="Count down, and count up"
          description="Watch the days, hours, minutes, and seconds fall away. Dates that already passed keep counting up, so anniversaries and milestones grow."
          delay={0.1}
        />
        <FeatureCard 
          icon="PenTool"
          title="Made yours"
          description="Rich gradients, icons, and tags for every event. Reframe any countdown into the units that mean something to you."
          delay={0.2}
        />
        <FeatureCard 
          icon="Calendar"
          title="Bring your dates with you"
          description="Import from your calendar and contact birthdays, and get a gentle local reminder before a countdown ends."
          delay={0.3}
        />
        <FeatureCard 
          icon="Lock"
          title="Private by default"
          description="No accounts, no servers, no analytics. Your dates never leave your device."
          delay={0.4}
        />
      </section>

      {/* Closing Section */}
      <section className="text-center max-w-2xl mx-auto py-12 md:py-24">
        <motion.h2 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="font-serif italic text-3xl md:text-4xl text-brand-cream leading-relaxed"
        >
          Add the first date you are looking forward to and see how much closer it already feels.
        </motion.h2>
      </section>
      
    </div>
  );
}